import { Component, DestroyRef, inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IAmanoOneAccessGroups,
  IAmanoOneLotConfigurationSettings,
  IAmanoOneCredentialTypeResponse,
  IAmanoOneCredentialSystemCodeResponse,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { NgFor } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-amano-one-new-parker',
  standalone: true,
  imports: [
    MatFormField,
    MatOptionModule,
    MatSelectModule,
    FormsModule,
    NgFor,
    MatCheckboxModule,
    MatTooltipModule,
    MatIconModule,
  ],
  templateUrl: './amano-one-new-parker.component.html',
  styleUrl: './amano-one-new-parker.component.scss',
})
export class AmanoOneNewParkerComponent implements OnInit {
  accessGroupId: string = '';
  amanoOneConfigSettings: any = {};
  createAmanoOneProfile: boolean = true;
  credentialTypeSettingValue: string = '';
  credentialCodeSettingValue: string = '';
  credentialCodeDisabled: boolean = true;
  credentialTypesList: IAmanoOneCredentialTypeResponse[] = [];
  credentialCodesList: IAmanoOneCredentialSystemCodeResponse[] = [];
  credentialTypeId: string = '';
  credentialTypeNoSystemCodeFlag: boolean = false;
  credentialTypesLoadFailed: boolean = false;
  parkerCardNumber: string = '';

  @Input() set cardNumberFromParker(value: string) {
    this.parkerCardNumber = value;
    this.evaluateCreateAmanoDataReadyToSave();
  }

  @Output() broadcastParcsStatus = new EventEmitter<boolean>();
  @Output() broadcastCreateAmanoProfile = new EventEmitter<boolean>();
  @Output() broadcastAccessGroupId = new EventEmitter<string>();
  @Output() broadcastSystemCodeId = new EventEmitter<string>();
  @Output() broadcastCredentialTypeId = new EventEmitter<number>();
  @Output() broadcastCredentialSystemType = new EventEmitter<string>();
  @Output() broadcastDataIsInvalid = new EventEmitter<boolean>();

  amanoOneAccessGroups: IAmanoOneAccessGroups[] = [];

  //Services
  _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  _appGeneralStateService = inject(AppGeneralStateService);

  constructor() {}

  ngOnInit() {
    this.evaluateCreateAmanoDataReadyToSave();
    this.broadcastCreateAmanoProfile.emit(this.createAmanoOneProfile);
    this.amanoOneConfigSettings = JSON.parse(
      this._appGeneralStateService.currentLotParcsConfiguration.settings.toLowerCase(),
    );
    this.getAmanoOneAccessGroupsByFacilityId(this.amanoOneConfigSettings.facilityid);
    this.reloadCredentialTypes();
  }

  reloadCredentialTypes(): void {
    this.getCredentialTypeList(this._appGeneralStateService.currentLotNumber).then((res) => {
      this.credentialTypesList = res;
    });
  }

  evaluateCreateAmanoDataReadyToSave(): boolean {
    // Set ready to save = true, and then fail any of the checks below
    let isReadyToSave: boolean = true;

    try {
      // is Access Group Selected?
      if (this.accessGroupId == '' || this.accessGroupId == null || this.accessGroupId == undefined) {
        throw new Error('Access Group not selected');
      }

      // If the card number is blank, then thes crednetialType and credentialSystemCode are not enabled
      if (this.parkerCardNumber != '' && this.parkerCardNumber != null && this.parkerCardNumber != undefined) {
        // is Credential Type Selected?
        if (this.credentialTypeId == '' || this.credentialTypeId == null || this.credentialTypeId == undefined) {
          throw new Error('Credential Type not selected');
        }

        // if Credential Type requires System Code, is System Code selected?
        if (this.credentialTypeId == '7') {
          //requuires system code
          if (
            this.credentialCodeSettingValue == '' ||
            this.credentialCodeSettingValue == null ||
            this.credentialCodeSettingValue == undefined
          ) {
            throw new Error('Credential System Code not selected');
          }
        }
      }
      // Based on above, enable or disable parcs data save ability
    } catch (err) {
      console.error('Error: Amano One Data not ready to save:', err.message);
      isReadyToSave = false;
      this.broadcastDataIsInvalid.emit(true);
    }
    this.broadcastDataIsInvalid.emit(!isReadyToSave);
    return isReadyToSave;
  }

  onSaveAmanoProfileChange(): void {
    this.broadcastCreateAmanoProfile.emit(this.createAmanoOneProfile);

    if (this.createAmanoOneProfile) {
      this.broadcastDataIsInvalid.emit(false);
      this.broadcastParcsStatus.emit(false);
      this.broadcastAccessGroupId.emit(this.accessGroupId);
      this.broadcastCredentialSystemType.emit('');
      this.broadcastSystemCodeId.emit('');
      this.broadcastCredentialTypeId.emit(0);
    } else {
      this.accessGroupId = '';
      this.broadcastDataIsInvalid.emit(true);
      this.broadcastParcsStatus.emit(true);
      this.broadcastAccessGroupId.emit(this.accessGroupId);
      this.broadcastCredentialSystemType.emit('');
      this.broadcastSystemCodeId.emit('');
      this.broadcastCredentialTypeId.emit(0);
    }
    this.credentialCodeSettingValue = '';
    this.credentialTypeId = '';
    this.credentialCodeSettingValue = '';
  }

  onAccessGroupChange(newAccessGroupId: any): void {
    this.accessGroupId = newAccessGroupId;
    //this.broadcastParcsStatus.emit(this.createAmanoOneProfile);
    this.evaluateCreateAmanoDataReadyToSave();
    this.broadcastAccessGroupId.emit(this.accessGroupId);
  }

  getAmanoOneAccessGroupsByFacilityId(facilityId: string): void {
    this._parcsDataService
      .getAmanoOneParcsAccessGroupsByFacilityId(facilityId)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.amanoOneAccessGroups = res;
        },
        error: (err: any) => {
          this.amanoOneAccessGroups = [];
        },
        complete: () => {},
      });
  }

  onCredentialTypeChange(event: any): void {
    this.evaluateCreateAmanoDataReadyToSave();
    // If no credential type is selected, clear all related fields and broadcasts
    if (this.credentialTypeId == '') {
      this.broadcastCredentialSystemType.emit('');
      this.credentialCodesList = [];
      this.credentialCodeSettingValue = '';
      this.broadcastSystemCodeId.emit('');
      this.broadcastCredentialTypeId.emit(0);
      return;
    }

    this.broadcastSystemCodeId.emit('');

    // Check if the selected Credential Type has No System Code flag set
    // True indicates that this credential type does not use a system code
    const noSystemCodeFlag: boolean = this.credentialTypesList.find(
      (type) => type.credentialTypeId === +this.credentialTypeId,
    ).noSystemCode;
    this.credentialTypeNoSystemCodeFlag = noSystemCodeFlag;
    this.credentialCodeSettingValue = '';

    // If No System Code is selected, clear the system code list and disable the dropdown
    // Broadcast credential type and null/empty system code to add parker parent component
    if (noSystemCodeFlag) {
      this.credentialCodesList = [];
      this.credentialCodeDisabled = true;
      this.broadcastCredentialSystemType.emit(''); // Indicate no system code is needed
      this.broadcastCredentialTypeId.emit(+this.credentialTypeId);
    } else {
      // If System Code is required, get the system code list for the selected credential type

      // Get the list of system codes
      if (this.credentialTypeId != undefined && this.credentialTypeId != null && this.credentialTypeId != '') {
        this.getCredentialSystemCodeList(this._appGeneralStateService.currentLotNumber, +this.credentialTypeId).then(
          (res) => {
            this.credentialCodesList = res;
          },
        );
        this.credentialCodeSettingValue = '';
        this.broadcastCredentialTypeId.emit(+this.credentialTypeId);
        this.broadcastCredentialSystemType.emit(
          this.credentialTypesList.find((type) => type.credentialTypeId == +this.credentialTypeId)
            ?.credentialTypeName || '',
        );
      } else {
        this.broadcastCredentialSystemType.emit('');
        this.broadcastCredentialTypeId.emit(0);
        this.credentialCodesList = [];
        this.credentialCodeSettingValue = '';
      }
    }
  }

  onCredentialCodeChange(event: any): void {
    this.evaluateCreateAmanoDataReadyToSave();
    if (
      this.credentialCodeSettingValue != undefined &&
      this.credentialCodeSettingValue != null &&
      this.credentialCodeSettingValue != ''
    ) {
      this.broadcastSystemCodeId.emit(this.credentialCodeSettingValue);
    } else {
      this.broadcastSystemCodeId.emit('-1');
      this.credentialCodeSettingValue = '';
    }
  }

  // Gets the list of Credential Types from Amano One PARCS system.
  async getCredentialTypeList(lotNumber: string): Promise<IAmanoOneCredentialTypeResponse[]> {
    let credentialTypes: IAmanoOneCredentialTypeResponse[] = [];

    try {
      credentialTypes = await firstValueFrom(this._parcsDataService.AmanoOne_CredentialType_GetList(lotNumber));
      this.credentialTypesLoadFailed = false;
    } catch (err) {
      credentialTypes = [];
      this.credentialTypesLoadFailed = true;
    }
    return credentialTypes;
  }

  // Gets the list of Credential System Codes from Amano One PARCS system.
  async getCredentialSystemCodeList(
    lotNumber: string,
    credentialTypeId: number,
  ): Promise<IAmanoOneCredentialSystemCodeResponse[]> {
    let credentialSystemCodes: IAmanoOneCredentialSystemCodeResponse[] = [];

    try {
      credentialSystemCodes = await firstValueFrom(
        this._parcsDataService.AmanoOne_CredentialSystemCode_GetList(lotNumber, credentialTypeId),
      );
    } catch (err) {
      credentialSystemCodes = [];
    }
    return credentialSystemCodes;
  }
}
